import { useTheme } from "@amx/ux-twoui-theming";
import { Link, Stack, StackItem, Text } from "@fluentui/react";
import React from "react";
import { validateProps } from "../../../shared/props";
import { InteractiveElementProps } from "../../../shared/props";
import { IconName } from "../../../styles/icons";
import { getRTLIcon, isRTL } from "../../../styles/language";
import { Icon } from "../../Icon/Icon";
import { HyperlinkTarget } from "../../Links/shared/props";
import {
    getIconAlertBadgeStyles,
    getIconGeneralStyles,
    getInfoColumnLinkStyle,
    getInfoColumnLinkStyles,
    getInfoColumnStyles,
    getInfoGeneralStyles
} from "./BannerInfoColumn.styles";

interface BannerInfoColumnHrefProps {
    /**
     * ctaUrl is a string that represents the URL that will be used for the CTA
     */
    readonly ctaUrl: string;

    /**
     * Target determines whether the link opens in the same place or in a new window or tab, depending on
     * browser configuration.
     */
    readonly target?: HyperlinkTarget;

    readonly onClick?: undefined;
}

interface BannerInfoColumnOnClickProps {
    /**
     * method to handle when the CTA is clicked
     */
    readonly onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;

    readonly ctaUrl?: undefined;
}

export type BannerInfoColumnBadge = "Alert";

interface BannerInfoColumnDataProps extends InteractiveElementProps {
    /**
     * iconName is a string that represents the name of the icon to use from Fabric
     * list of Fluent UI Icons: https://developer.microsoft.com/en-us/fluentui#/styles/web/icons
     */
    readonly iconName: IconName;
    /**
     * ctaText is a string that represents the text that will be used for the CTA
     */
    readonly ctaText: string;
    /**
     * infoText is a string that represents the additional info presented in the infoColumn
     */
    readonly infoText: string;

    /**
     * A badge to display in the corner of the icon.
     */
    readonly badge?: BannerInfoColumnBadge;

    /**
     * WARNING: This is an experimental prop intended for use by the Subscriptions team. Do not
     * use this prop beyond that experiment until we have more data. See their scenario here:
     * https://microsoft.visualstudio.com/OS/_workitems/edit/33989410. If prop is true, use the
     * hyperlink-stylized version of BannerInfoColumn.
     */
    readonly isHyperlinkVariant?: boolean;
}

export type BannerInfoColumnProps = BannerInfoColumnDataProps & (BannerInfoColumnHrefProps | BannerInfoColumnOnClickProps);

export const BannerInfoColumn: React.FC<BannerInfoColumnProps> = ({
    iconName,
    ctaText,
    ctaUrl,
    onClick,
    infoText,
    badge,
    isHyperlinkVariant,
    ...otherProps
}) => {
    validateProps("BannerInfoColumn", otherProps);

    const theme = useTheme();
    const rtl = isRTL();
    let badgeElements;

    switch (badge) {
        case "Alert":
            badgeElements = (
                <Stack styles={getIconAlertBadgeStyles(rtl)}>
                    <Icon iconName="StatusCircle7" color="errorIcon" size="32px" />
                    <Icon iconName="StatusWarning" color="white" size="24px" />
                </Stack>
            );
            break;
        default:
            badgeElements = <></>;
    }

    return (
        <Stack horizontal={true} styles={getInfoColumnStyles()}>
            <StackItem>
                <Link href={ctaUrl} onClick={onClick} style={getInfoColumnLinkStyle()} styles={getInfoColumnLinkStyles(theme, iconName, isHyperlinkVariant)} {...otherProps}>
                    {badgeElements}
                    <Stack horizontal={true} styles={getIconGeneralStyles(iconName)}>
                        <Icon iconName="FullCircleMask" size="32px" />
                        <Icon iconName={iconName} size="16px" />
                    </Stack>
                    <Stack styles={getInfoGeneralStyles(isHyperlinkVariant)}>
                        <Text className="infoColumnCta" block={true} variant="small">
                            {ctaText}
                        </Text>
                        <Text className="infoColumnInfoText" block={true} variant="small">{infoText}</Text>
                    </Stack>
                </Link>
            </StackItem>
            {isHyperlinkVariant && (
                <StackItem styles={getInfoColumnLinkStyles(theme, iconName, isHyperlinkVariant)}>
                    <Icon iconName={getRTLIcon("ChevronRight")} size="12px" />
                </StackItem>
            )}
        </Stack>
    );
};
